<script setup lang="ts">
// The live log of the environment's current step (FR-44), following new lines unless the reader scrolls up.
import { nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useContext } from '../context';
import { LogStream } from '../logs/logStream';
import { apply, emptyModel, type LogModel } from '../logs/logModel';

const props = defineProps<{
  environment: string;
  /** The environment is reconciling or an action is in flight: an ended stream is retried shortly (protocol: idle while in progress means "retry shortly"). */
  active?: boolean;
}>();

const STEPS = ['current', 'db-capture', 'db-restore', 'db-replace', 'db-prepare', 'db-gate', 'db-audit', 'app-restore', 'app-setup',
  'cd-restore', 'ts-restore', 'cs-restore', 'cleanup', 'htaccess'];
const step = ref('current');
let retry: number | undefined;

const { cfg, auth } = useContext();
const model = reactive<LogModel>(emptyModel());
const follow = ref(true);
const box = ref<HTMLElement>();
let stream: LogStream | undefined;

/** keep = a retry while the environment is busy: the shown log stays, the next Job's log is appended. */
function start(keep = false) {
  window.clearTimeout(retry);
  stream?.stop();
  if (!keep) Object.assign(model, emptyModel());
  stream = new LogStream({
    base: cfg.logsBase,
    environment: props.environment,
    action: step.value,
    auth,
    onEvent: (e) => {
      apply(model, e);
      if (e.kind === 'frame' && follow.value) void nextTick(scrollToEnd);
      if (e.kind === 'state' && e.state === 'ended') scheduleRetry();
    },
  });
  stream.start();
}

function scrollToEnd() {
  if (box.value) box.value.scrollTop = box.value.scrollHeight;
}

function onScroll() {
  const el = box.value;
  if (!el) return;
  const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  if (!atEnd && follow.value) follow.value = false;
  if (atEnd && !follow.value) follow.value = true;
}

watch(follow, (f) => f && nextTick(scrollToEnd));
function scheduleRetry() {
  window.clearTimeout(retry);
  if (props.active) retry = window.setTimeout(() => start(true), 5000);
}
watch(
  () => props.active,
  (a) => {
    if (a && model.state === 'ended') scheduleRetry();
  },
);
watch(step, () => start());
onMounted(() => start());
onBeforeUnmount(() => {
  window.clearTimeout(retry);
  stream?.stop();
});

const STATE_TEXT: Record<string, string> = {
  connecting: 'connecting…',
  open: 'live',
  reconnecting: 'reconnecting…',
  ended: 'ended',
  failed: 'stopped',
  stopped: 'stopped',
};
</script>

<template>
  <section class="card log-card" aria-labelledby="log-h">
    <div class="log-bar">
      <h2 id="log-h">Live log</h2>
      <span class="badge" :class="model.state === 'open' ? 'tone-ok' : model.state === 'failed' ? 'tone-bad' : model.state === 'ended' || model.state === 'stopped' ? 'tone-idle' : 'tone-progress'" data-testid="log-state">
        <span class="sym" aria-hidden="true">{{ model.state === 'open' ? '●' : model.state === 'failed' ? '✕' : model.state === 'ended' ? '■' : '…' }}</span>{{ STATE_TEXT[model.state] ?? model.state }}
      </span>
      <span v-if="model.job" class="mono">step {{ model.job.step }} · {{ model.job.job }}</span>
      <span v-if="model.container" class="mono muted">container {{ model.container }}</span>
      <label class="inline muted" style="margin-left: auto; font-weight: 400" for="log-step">Step</label>
      <select id="log-step" v-model="step">
        <option v-for="s in STEPS" :key="s" :value="s">{{ s === 'current' ? 'current (follows the phase)' : s }}</option>
      </select>
      <label class="inline" style="font-weight: 400"><input v-model="follow" type="checkbox" /> Follow</label>
      <button v-if="model.state === 'ended' || model.state === 'failed' || model.state === 'stopped'" type="button" class="sm" @click="start()">Reconnect</button>
    </div>
    <p v-if="model.state === 'failed' && model.sentence" class="alert" role="alert" data-testid="log-error">{{ model.sentence }}</p>
    <div ref="box" class="log" role="log" aria-live="off" aria-label="Live log lines" tabindex="0" data-testid="log" @scroll="onScroll">
      <div v-if="model.dropped" class="meta">… {{ model.dropped }} earlier lines not shown</div>
      <template v-for="e in model.entries" :key="e.id">
        <div v-if="e.kind === 'line'">{{ e.text }}<span v-if="e.truncated" class="meta"> [line truncated]</span></div>
        <div v-else-if="e.kind === 'boundary'" class="boundary">{{ e.text }}</div>
        <div v-else-if="e.kind === 'outcome'" :class="e.ok ? 'outcome-ok' : 'outcome-bad'">{{ e.text }}</div>
        <div v-else class="meta">{{ e.text }}</div>
      </template>
      <div v-if="!model.entries.length" class="meta">{{ model.state === 'open' ? 'waiting for output…' : '' }}</div>
    </div>
    <p class="sr-only" aria-live="polite">{{ model.outcome?.text }}</p>
  </section>
</template>
